import type { OrderablePackage } from './orders.js';

/**
 * Which channels an order may use — MYP-87.
 *
 * `channels` returns every channel the vendor has, across twelve warehouses (204 live on
 * 2026-09-24), domestic ones included. `neworder_getChannel` returns the ones a new order can
 * actually use, and needs three things to answer: the package ids, one warehouse and one goods
 * type. Missing any of the three, it refuses with `code 2` — which is why an earlier probe that
 * sent only some of them recorded it as unusable. Live, for warehouse 1 GENERAL it returned 31
 * channels: 7 of them absent from `channels` altogether (sea freight, mostly), and 18 of the
 * `channels` entries for that warehouse left out. It is the list the picker offers.
 */

/** What the channel lookup is asked about: one warehouse, one goods type, these packages. */
export interface ChannelScope {
  readonly warehouseId: string;
  readonly goodsType: string;
  readonly packageIds: readonly string[];
}

/**
 * Whether the selected packages can be looked up at all, and if not, why not.
 *
 * - `noPackages`: nothing selected, so there is nothing to ask about.
 * - `unknown`: a package has no warehouse or no goods type on record. The lookup needs both, and
 *   guessing GENERAL would offer channels that may refuse the parcel.
 * - `mixedWarehouses`: an order ships from one warehouse, and the lookup takes exactly one.
 * - `mixedGoodsTypes`: the lookup takes exactly one goods type, and how the vendor treats an order
 *   mixing general and sensitive goods is not documented. Blocked until it is, rather than
 *   quietly sending one type for both.
 */
export type ChannelScopeResult =
  | { readonly kind: 'ok'; readonly scope: ChannelScope }
  | { readonly kind: 'noPackages' }
  | { readonly kind: 'unknown'; readonly trackingNumbers: readonly string[] }
  | { readonly kind: 'mixedWarehouses'; readonly warehouseIds: readonly string[] }
  | { readonly kind: 'mixedGoodsTypes'; readonly goodsTypes: readonly string[] };

function distinct(values: readonly string[]): readonly string[] {
  return values.filter((value, index) => values.indexOf(value) === index);
}

export function channelScopeFor(packages: readonly OrderablePackage[]): ChannelScopeResult {
  if (packages.length === 0) return { kind: 'noPackages' };

  const unknown = packages.filter((item) => !item.warehouseId || !item.goodsType);
  if (unknown.length > 0) {
    return { kind: 'unknown', trackingNumbers: unknown.map((item) => item.trackingNumber) };
  }

  const warehouseIds = distinct(packages.map((item) => item.warehouseId!));
  if (warehouseIds.length > 1) return { kind: 'mixedWarehouses', warehouseIds };

  const goodsTypes = distinct(packages.map((item) => item.goodsType!.toUpperCase()));
  if (goodsTypes.length > 1) return { kind: 'mixedGoodsTypes', goodsTypes };

  return {
    kind: 'ok',
    scope: {
      warehouseId: warehouseIds[0]!,
      goodsType: goodsTypes[0]!,
      packageIds: packages.map((item) => item.id),
    },
  };
}

/**
 * The cache key for a scope's channel list. Package ids are left out on purpose: live, the list
 * depended only on the warehouse and goods type (a made-up package id returned the same 31), so
 * ticking another parcel from the same warehouse does not fetch again.
 */
export function channelScopeKey(scope: Pick<ChannelScope, 'warehouseId' | 'goodsType'>): string {
  return `${scope.warehouseId}|${scope.goodsType.toUpperCase()}`;
}

/**
 * Drops channel rows no customer should be offered: not flagged for the front end, not active, or
 * domestic. Live, every row passes the first two today and six fail the third — the checks are
 * here so a channel the vendor switches off stops being offered without a release.
 */
export function offerableChannelRows(rows: readonly unknown[]): readonly unknown[] {
  return rows.filter((row) => {
    if (typeof row !== 'object' || row === null) return false;
    const record = row as Readonly<Record<string, unknown>>;
    const flag = (key: string) =>
      typeof record[key] === 'string' ? (record[key] as string).trim().toUpperCase() : undefined;
    return flag('showfront') !== 'N' && flag('status') !== 'N' && flag('type') !== 'DOMESTIC';
  });
}
